import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import Images from '../../assets/images/verify.svg';

const getRedirectPath = (userData) => {
    const roleData = userData?.role;
    const isSuperAdmin = (
        (typeof roleData === 'object' && roleData?.privileges?.includes('*')) ||
        (typeof roleData === 'string' && (roleData.toLowerCase() === 'admin' || roleData.toLowerCase() === 'superadmin'))
    );

    if (isSuperAdmin) {
        return '/dashboard';
    }

    if (typeof roleData === 'object' && Array.isArray(roleData.privileges) && roleData.privileges.length > 0) {
        const resourceToRouteMap = {
            dashboard: '/dashboard',
            users: '/users-list',
            expertise: '/expertise-list',
            astrologers: '/astrologer-list',
            bookings: '/booking-list',
            notifications: '/notification-list',
            languages: '/language-list',
            skills: '/skill-list',
            astro_tags: '/astro-tag-management',
            role_management: '/role-management',
            user_management: '/user-management',
            offers: '/offer-list',
            lead_management: '/leads-management',
            student_management: '/student-management',
            course_management: '/course-management',
            category_management: '/category-management',
            course_banners: '/course-banners',
            instructor_leads: '/instructor-leads',
            about_us: '/about-us',
            privacy_policy: '/privacy-policy',
            terms_conditions: '/terms-conditions',
            refund_policy: '/refund-policy',
            course_specific_reviews: '/course-specific',
            generic_reviews: '/generic-reviews',
            course_specific_faqs: '/course-specificfaq',
            generic_faqs: '/generic-faq',
            refer_earn_faqs: '/refer-faq',
            education_coupons: '/eduction-modulecoupon',
            consultation_coupons: '/consultation-coupon',
            google_pay_coupons: '/googlepay-coupon',
            consultation_vouchers: '/consultation-voucher',
            top_banners: '/top-banners',
            consultation_type_banners: '/consultation-type',
            service_banners: '/service-banners',
            user_feedback: '/user-feedback',
            astrologer_tickets: '/astrologer-support',
            user_tickets: '/user-support',
            callback_requests: '/callback-requests',
            daily_recharge_report: '/daily-recharge',
            invoice_reports: '/invoice-reports',
            session_report: '/session-reports',
            coupon_usage_report: '/coupon-usesreport',
            credit_note_report: '/credit-reports',
            payout_report: '/payout-reports',
            course_selling_report: '/course-selling-report',
            wallet_report: '/wallet-report',
        };

        for (const privilege of roleData.privileges) {
            const resource = privilege.split(':')[0];
            if (resourceToRouteMap[resource]) {
                return resourceToRouteMap[resource];
            }
        }
    }

    return '/dashboard';
};

function LoginOTPVerification() {
    const navigate = useNavigate();
    const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm();
    const [isResending, setIsResending] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [timer, setTimer] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const [userEmail, setUserEmail] = useState('');
    const inputRefs = useRef([]);

    // Watch all OTP inputs
    const code1 = watch('code1');
    const code2 = watch('code2');
    const code3 = watch('code3');
    const code4 = watch('code4');

    // Check if user has email from login
    useEffect(() => {
        const email = localStorage.getItem("vedhvidhi-admin-temp-email");

        if (!email) {
            toast.error("Please login first");
            navigate(process.env.PUBLIC_URL + "/sign-in");
            return;
        }

        setUserEmail(email);
    }, [navigate]);

    // Timer for resend OTP
    useEffect(() => {
        if (timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setCanResend(true);
        }
    }, [timer]);

    // Removed auto-submit - API will only be called on button click

    const onSubmit = (data) => {
        setIsVerifying(true);
        const otp = parseInt(`${data.code1}${data.code2}${data.code3}${data.code4}`);
        const email = localStorage.getItem("vedhvidhi-admin-temp-email");
        const isSpecialAdmin = localStorage.getItem("vedhvidhi-admin-is-special") === 'true';

        if (!email) {
            toast.error("Session expired. Please login again.");
            navigate(process.env.PUBLIC_URL + "/sign-in");
            setIsVerifying(false);
            return;
        }

        // Determine which API endpoint to use based on admin type
        const verifyUrl = isSpecialAdmin
            ? `${process.env.REACT_APP_BASEURL}/admin/verify-otp`
            : `${process.env.REACT_APP_BASEURL}/admin/member/verify-otp`;

        axios
            .post(verifyUrl, {
                email: email,
                otp: otp
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then((response) => {
                const isSuccess = response?.data?.status || response?.data?.success;
                if (!isSuccess) {
                    toast.error(response?.data?.message || "OTP verification failed");
                    setIsVerifying(false);
                    return;
                }

                // Get token and user data from response
                // Handle both response formats: accessToken/userData or token/user

                let accessToken = response.data.accessToken || response?.data?.data?.token;
                let userData = response.data.userData || response?.data?.data?.user;

                if (!accessToken || !userData) {
                    toast.error("OTP verification response missing token or user data");
                    setIsVerifying(false);
                    return;
                }

                // Store final token and user data
                localStorage.setItem("vedhvidhi-admin-token", accessToken);
                localStorage.setItem("vedhvidhi-admin-data", JSON.stringify(userData));

                // Clean up temporary data
                localStorage.removeItem("vedhvidhi-admin-temp-email");
                localStorage.removeItem("vedhvidhi-admin-is-special");

                toast.success(response?.data?.message || "OTP verified successfully!");

                // Redirect to appropriate dashboard
                const redirectPath = getRedirectPath(userData);
                window.location.href = process.env.PUBLIC_URL + redirectPath;
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "OTP verification failed");
                setIsVerifying(false);
            });
    };

    const handleResendOTP = () => {
        if (!canResend || isResending) return;

        setIsResending(true);
        const email = localStorage.getItem("vedhvidhi-admin-temp-email");

        if (!email) {
            toast.error("Session expired. Please login again.");
            navigate(process.env.PUBLIC_URL + "/sign-in");
            setIsResending(false);
            return;
        }

        // Resend OTP API - same for both admin and member
        const resendUrl = `${process.env.REACT_APP_BASEURL}/admin/resend-otp`;

        axios
            .post(resendUrl, {
                email: email
            }, {
                headers: {
                    'Content-Type': 'application/json'
                }
            })
            .then((response) => {
                const isSuccess = response?.data?.status || response?.data?.success;
                if (!isSuccess) {
                    toast.error(response?.data?.message || "Failed to resend OTP");
                    setIsResending(false);
                    return;
                }

                toast.success(response?.data?.message || "OTP resent successfully!");
                setTimer(60);
                setCanResend(false);
                setIsResending(false);

                // Clear all OTP inputs
                setValue('code1', '');
                setValue('code2', '');
                setValue('code3', '');
                setValue('code4', '');

                // Focus on first input
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            })
            .catch((error) => {
                toast.error(error?.response?.data?.message || "Failed to resend OTP");
                setIsResending(false);
            });
    };

    const handleInputChange = (e, index) => {
        const { value } = e.target;

        // Only allow single digit
        if (/^\d$/.test(value)) {
            setValue(`code${index}`, value);
            // Move to next input
            if (index < 4 && inputRefs.current[index]) {
                inputRefs.current[index].focus();
            }
        } else if (value === '') {
            // Allow clearing the input
            setValue(`code${index}`, '');
        } else {
            // Prevent non-digit input
            e.target.value = '';
            setValue(`code${index}`, '');
        }
    };

    const handleKeyDown = (e, index) => {
        const { key, target } = e;

        // Handle Backspace
        if (key === 'Backspace') {
            if (target.value) {
                // If input has value, clear it and stay in same field
                setValue(`code${index}`, '');
                target.value = '';
            } else {
                // If input is empty, move to previous field
                e.preventDefault();
                if (index > 1 && inputRefs.current[index - 2]) {
                    inputRefs.current[index - 2].focus();
                    // Clear previous field value
                    setValue(`code${index - 1}`, '');
                    inputRefs.current[index - 2].value = '';
                }
            }
        }
        // Handle Arrow Left - move to previous input
        else if (key === 'ArrowLeft') {
            e.preventDefault();
            if (index > 1 && inputRefs.current[index - 2]) {
                inputRefs.current[index - 2].focus();
            }
        }
        // Handle Arrow Right - move to next input
        else if (key === 'ArrowRight') {
            e.preventDefault();
            if (index < 4 && inputRefs.current[index]) {
                inputRefs.current[index].focus();
            }
        }
        // Handle Delete key
        else if (key === 'Delete') {
            setValue(`code${index}`, '');
            target.value = '';
        }
        // Prevent non-numeric keys (except navigation keys)
        else if (!/^[0-9]$/.test(key) &&
            !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'].includes(key)) {
            e.preventDefault();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const pastedData = e.clipboardData.getData('text').trim().replace(/\s/g, '').replace(/-/g, '').replace(/[^0-9]/g, '');

        // Check if pasted data has at least 4 digits
        if (pastedData.length >= 4) {
            const digits = pastedData.substring(0, 4).split('');

            // Set values directly using setValue and update DOM
            digits.forEach((digit, idx) => {
                if (idx < 4 && /^\d$/.test(digit)) {
                    const inputIndex = idx + 1;
                    // Update form state
                    setValue(`code${inputIndex}`, digit, { shouldValidate: true, shouldDirty: true });

                    // Update DOM directly and trigger change event
                    if (inputRefs.current[idx]) {
                        const input = inputRefs.current[idx];
                        input.value = digit;

                        // Manually trigger onChange to ensure form state updates
                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
                        nativeInputValueSetter.call(input, digit);
                        const event = new Event('input', { bubbles: true });
                        input.dispatchEvent(event);
                    }
                }
            });

            // Force re-render by triggering a state update
            setTimeout(() => {
                // Focus on last input
                if (inputRefs.current[3]) {
                    inputRefs.current[3].focus();
                }
                toast.success("OTP pasted successfully!");
            }, 50);
        } else {
            toast.error("Please paste a valid 4-digit code");
        }
    };

    const handleFocus = (e) => {
        e.target.style.borderColor = '#4299e1';
        e.target.style.backgroundColor = '#fff';
        e.target.style.boxShadow = '0 0 0 3px rgba(66, 153, 225, 0.1)';
        // Select all text on focus for easy replacement
        e.target.select();
    };

    const handleBlur = (e, index) => {
        const hasError = errors[`code${index}`];
        e.target.style.borderColor = hasError ? '#dc3545' : '#e2e8f0';
        e.target.style.backgroundColor = '#f8f9fa';
        e.target.style.boxShadow = 'none';
    };

    return (
        <>
            <Toaster position="top-center" reverseOrder={true} duration="10000" />
            <div className="col-lg-6 d-flex justify-content-center align-items-center border-0 rounded-lg auth-h100">
                <div className="w-100 p-3 p-md-5 card border-0 shadow-sm" style={{ maxWidth: '32rem' }}>
                    <form onSubmit={handleSubmit(onSubmit)} className="row g-1 p-3 p-md-4">
                        <div className="col-12 text-center mb-5">
                            <div className="mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
                                <img
                                    src={Images}
                                    className="w240 mb-4"
                                    alt="Verification"
                                    style={{ maxWidth: '200px', height: 'auto' }}
                                />
                            </div>
                            <h1 className="mb-3" style={{ fontSize: '2rem', fontWeight: '600', color: '#2d3748' }}>
                                Two-Step Verification
                            </h1>
                            <p className="text-muted mb-2" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                                We've sent a verification code to
                            </p>
                            {userEmail && (
                                <p className="mb-3" style={{ fontSize: '1rem', fontWeight: '600', color: '#4a5568' }}>
                                    {userEmail}
                                </p>
                            )}
                            <p className="text-muted" style={{ fontSize: '0.9rem', lineHeight: '1.6' }}>
                                Enter the 4-digit code below to complete your login
                            </p>
                        </div>

                        {/* OTP Input Fields */}
                        <div className="col-12 mb-4">
                            <div className="d-flex justify-content-between gap-2" style={{ maxWidth: '320px', margin: '0 auto' }}>
                                {[1, 2, 3, 4].map((index) => (
                                    <div key={index} style={{ flex: '1' }}>
                                        <input
                                            {...register(`code${index}`, {
                                                required: "Code is required.",
                                                pattern: {
                                                    value: /^\d$/,
                                                    message: "Only digits allowed"
                                                }
                                            })}
                                            type="text"
                                            inputMode="numeric"
                                            id={`code${index}`}
                                            maxLength="1"
                                            className="form-control form-control-lg text-center"
                                            style={{
                                                fontSize: '1.5rem',
                                                fontWeight: '600',
                                                height: '60px',
                                                border: errors[`code${index}`]
                                                    ? '2px solid #dc3545'
                                                    : '2px solid #e2e8f0',
                                                borderRadius: '8px',
                                                transition: 'all 0.3s ease',
                                                backgroundColor: '#f8f9fa'
                                            }}
                                            placeholder="0"
                                            onChange={(e) => handleInputChange(e, index)}
                                            onKeyDown={(e) => handleKeyDown(e, index)}
                                            onPaste={handlePaste}
                                            onFocus={handleFocus}
                                            onBlur={(e) => handleBlur(e, index)}
                                            ref={(el) => {
                                                inputRefs.current[index - 1] = el;
                                            }}
                                            disabled={isVerifying}
                                            autoComplete="off"
                                        />
                                        {errors[`code${index}`] && (
                                            <span className="text-danger" style={{ fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>
                                                {errors[`code${index}`].message}
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Verify Button */}
                        <div className="col-12 text-center mt-4">
                            <button
                                type="submit"
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (!code1 || !code2 || !code3 || !code4) {
                                        toast.error("Please enter all 4 digits of the OTP");
                                        return;
                                    }
                                    if (isVerifying) {
                                        return;
                                    }
                                    const formData = {
                                        code1,
                                        code2,
                                        code3,
                                        code4
                                    };
                                    onSubmit(formData);
                                }}
                                className="btn btn-lg btn-block lift text-uppercase"
                                style={{
                                    minHeight: '50px',
                                    fontSize: '0.9rem',
                                    fontWeight: '600',
                                    letterSpacing: '0.5px',
                                    transition: 'all 0.3s ease',
                                    backgroundColor: (isVerifying || !code1 || !code2 || !code3 || !code4)
                                        ? '#e0e0e0'
                                        : '#ffd55d',
                                    color: (isVerifying || !code1 || !code2 || !code3 || !code4)
                                        ? '#9e9e9e'
                                        : '#212529',
                                    border: 'none',
                                    opacity: (isVerifying || !code1 || !code2 || !code3 || !code4) ? 0.6 : 1,
                                    cursor: (isVerifying || !code1 || !code2 || !code3 || !code4) ? 'not-allowed' : 'pointer',
                                    boxShadow: (isVerifying || !code1 || !code2 || !code3 || !code4)
                                        ? 'none'
                                        : '0 2px 8px rgba(255, 213, 93, 0.3)'
                                }}
                                disabled={isVerifying || !code1 || !code2 || !code3 || !code4}
                            >
                                {isVerifying ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Verifying...
                                    </>
                                ) : (
                                    'Verify OTP'
                                )}
                            </button>
                        </div>

                        {/* Resend OTP Section */}
                        <div className="col-12 text-center mt-4">
                            <div style={{ fontSize: '0.9rem', color: '#718096' }}>
                                <span>Haven't received the code? </span>
                                {canResend ? (
                                    <button
                                        type="button"
                                        onClick={handleResendOTP}
                                        disabled={isResending}
                                        className="btn btn-link p-0 text-primary"
                                        style={{
                                            textDecoration: 'none',
                                            fontWeight: '600',
                                            border: 'none',
                                            background: 'none',
                                            cursor: isResending ? 'not-allowed' : 'pointer',
                                            padding: '0',
                                            marginLeft: '4px',
                                            opacity: isResending ? 0.6 : 1
                                        }}
                                    >
                                        {isResending ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                                Sending...
                                            </>
                                        ) : (
                                            'Resend Code'
                                        )}
                                    </button>
                                ) : (
                                    <span className="text-secondary" style={{ fontWeight: '500' }}>
                                        Resend code in <strong>{timer}s</strong>
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Back to Login Link */}
                        <div className="col-12 text-center mt-3">
                            <button
                                type="button"
                                onClick={() => {
                                    localStorage.removeItem("vedhvidhi-admin-temp-email");
                                    localStorage.removeItem("vedhvidhi-admin-is-special");
                                    navigate(process.env.PUBLIC_URL + "/sign-in");
                                }}
                                className="btn btn-link text-secondary p-0"
                                style={{
                                    textDecoration: 'none',
                                    fontSize: '0.85rem',
                                    border: 'none',
                                    background: 'none',
                                    cursor: 'pointer'
                                }}
                            >
                                ← Back to Login
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default LoginOTPVerification;

